import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ChatService } from './chat.service';
import { ChatFileDTO, ChatMessageDTO } from '../models/chat-message-dto';
import { ChatMessagesReadByAllDTO } from '../models/chat-messages-read-by-all-dto';
import { ChatRoomMemberDTO } from '../models/chat-room-member-dto';
import { ChatUserSelectDTO } from '../models/chat-user-select-dto';

// CHAT_REVISION.md D2: dos eventos en vez del único ChatMessageReceived genérico de antes — el
// backend ahora dice explícitamente si es un mensaje nuevo o la edición/borrado de uno existente.
const MESSAGE_CREATED_EVENT = 'MessageCreated';
const MESSAGE_UPDATED_EVENT = 'MessageUpdated';
const CHAT_MESSAGES_READ_BY_ALL_EVENT = 'ChatMessagesReadByAll';
// CHAT.md §23.2: estado de conexión de usuarios.
const USER_PRESENCE_CHANGED_EVENT = 'UserPresenceChanged';
const SYNC_ROOMS_GROUPS_METHOD = 'SyncRoomsGroups';
const INITIAL_RECONNECT_DELAY_MS = 5_000;

/**
 * SignalR serializa en camelCase por defecto (a diferencia de la API REST, que usa PascalCase
 * vía PropertyNamingPolicy=null en Program.cs, solo aplicado a MVC). Este es el shape real que
 * llega por el hub — se adapta aquí a ChatMessageDTO para que el resto del chat no tenga que
 * saber de esta diferencia de formato entre transportes.
 */
interface ChatMessageReceivedPayload {
  idMessage: string;
  idChat: string;
  message: string;
  creationDate: string;
  senderName: string;
  idUser?: string;
  isMine: boolean;
  isEdited: boolean;
  isDeleted: boolean;
  isReadByAll: boolean;
  isSystem: boolean;
  isAudio: boolean;
  fileAttached?: {
    fileName: string;
    fileSize: number;
    fileExtension: string;
    fileUri: string;
  };
}

/** Mismo motivo que ChatMessageReceivedPayload: shape camelCase real del hub. */
interface ChatMessagesReadByAllPayload {
  idChat: string;
  messageIds: string[];
}

/** Mismo motivo que ChatMessageReceivedPayload: shape camelCase real del hub. */
interface UserPresenceChangedPayload {
  idUser: string;
  isOnline: boolean;
}

@Injectable({ providedIn: 'root' })
export class ChatRealtimeService {
  private readonly authService = inject(AuthService);
  private readonly chatService = inject(ChatService);
  private readonly messageReceivedSubject = new Subject<ChatMessageDTO>();
  private readonly messagesReadByAllSubject = new Subject<ChatMessagesReadByAllDTO>();
  private readonly connection: HubConnection;

  /**
   * Estado ÚNICO de conexión por usuario (CHAT.md §23.4), leído por los 4 sitios (room-list, cabecera
   * de conversación, modal de miembros, selectores) en vez de que cada uno tenga su propia suscripción.
   * Se alimenta con la foto de cada respuesta HTTP (seedOnlineStatus, llamado por los componentes tras
   * cargar salas/miembros/seleccionables) y con los eventos UserPresenceChanged en vivo.
   */
  private readonly onlineUsersState = signal<ReadonlyMap<string, boolean>>(new Map());

  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  private started = false;

  // true tras disconnect() (fin de sesión): evita que el onclose que dispara el propio stop()
  // vuelva a programar una reconexión con el token del usuario que acaba de salir.
  private stoppedByUser = false;

  // Cierre en curso de disconnect(): connect() lo espera antes de arrancar, porque start() sobre
  // una conexión que todavía no ha llegado a Disconnected lanza error.
  private stopping: Promise<void> = Promise.resolve();

  readonly messageReceived$: Observable<ChatMessageDTO> = this.messageReceivedSubject.asObservable();
  readonly messagesReadByAll$: Observable<ChatMessagesReadByAllDTO> = this.messagesReadByAllSubject.asObservable();

  constructor() {
    this.connection = new HubConnectionBuilder()
      .withUrl(`${environment.urlBase}hubs/chat`, {
        accessTokenFactory: () => this.authService.getToken() ?? ''
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    // MessageCreated y MessageUpdated comparten el mismo shape de payload y el mismo manejo: el
    // consumidor (ChatConversationComponent.onMessageReceived) ya distingue crear/editar/borrar por
    // IsEdited/IsDeleted sobre un único stream messageReceived$ — separar el evento de SignalR es solo
    // más claridad de protocolo, no cambia la lógica de negocio en el frontend.
    this.connection.on(MESSAGE_CREATED_EVENT, (payload: ChatMessageReceivedPayload) => this.onMessagePayload(payload));
    this.connection.on(MESSAGE_UPDATED_EVENT, (payload: ChatMessageReceivedPayload) => this.onMessagePayload(payload));

    // Sin notifyRoomsChanged(), a propósito: un acuse de "visto por todos" no cambia ni el preview
    // ni el no-leído de ninguna sala, así que no hay nada que recargar.
    this.connection.on(CHAT_MESSAGES_READ_BY_ALL_EVENT, (payload: ChatMessagesReadByAllPayload) => {
      this.messagesReadByAllSubject.next({
        IdChat: payload.idChat,
        MessageIds: payload.messageIds
      });
    });

    this.connection.on(USER_PRESENCE_CHANGED_EVENT, (payload: UserPresenceChangedPayload) => {
      this.setOnline(payload.idUser, payload.isOnline);
    });

    this.connection.onreconnected(() => this.syncRoomsGroups());
    this.connection.onclose(() => this.scheduleReconnect());

    // ChatService.rooms ya es el estado único de salas (CHAT_REVISION.md B8) e incluye los miembros
    // con su IsOnline (CHAT.md §23.3) en cada recarga — se reutiliza como fuente de la foto inicial en
    // vez de que cada componente de room-list/conversación tenga que llamar a seedOnlineStatus por su
    // cuenta.
    effect(() => {
      const rooms = this.chatService.rooms();
      untracked(() => this.seedOnlineStatus(rooms.flatMap(r => r.Members)));
    });
  }

  /**
   * Foto fija del estado de conexión de un usuario, resuelta contra la última respuesta HTTP conocida
   * o un evento en vivo posterior — lo que haya llegado último. No es una garantía en tiempo real para
   * un usuario con el que todavía no se comparte ninguna sala (CHAT.md §23.2/23.4).
   */
  isOnline(idUser: string): boolean {
    return this.onlineUsersState().get(idUser) ?? false;
  }

  /** Alimenta el estado con los `IsOnline` de una respuesta HTTP (GetRooms/GetRoomMembers/GetSelectableUsers). */
  seedOnlineStatus(users: Array<ChatRoomMemberDTO | ChatUserSelectDTO>): void {
    if (users.length === 0) return;

    const next = new Map(this.onlineUsersState());
    for (const user of users) next.set(user.IdUser, user.IsOnline);
    this.onlineUsersState.set(next);
  }

  private setOnline(idUser: string, isOnline: boolean): void {
    const next = new Map(this.onlineUsersState());
    next.set(idUser, isOnline);
    this.onlineUsersState.set(next);
  }

  private onMessagePayload(payload: ChatMessageReceivedPayload): void {
    const message: ChatMessageDTO = {
      IdMessage: payload.idMessage,
      IdChat: payload.idChat,
      Message: payload.message,
      CreationDate: payload.creationDate,
      SenderName: payload.senderName,
      IdUser: payload.idUser,
      IsMine: payload.isMine,
      IsEdited: payload.isEdited,
      IsDeleted: payload.isDeleted,
      IsReadByAll: payload.isReadByAll,
      IsSystem: payload.isSystem,
      IsAudio: payload.isAudio,
      FileAttached: payload.fileAttached ? {
        FileName: payload.fileAttached.fileName,
        FileSize: payload.fileAttached.fileSize,
        FileExtension: payload.fileAttached.fileExtension,
        FileUri: payload.fileAttached.fileUri
      } as ChatFileDTO : undefined
    };

    this.messageReceivedSubject.next(message);
    // Refresca la lista de salas (preview + no leído) y, con ello, el badge del navbar.
    this.chatService.notifyRoomsChanged();
  }

  /** Se llama una única vez, al arrancar la sesión (default-layout), no al abrir la modal del chat. */
  connect(): void {
    if (this.started) return;
    this.started = true;
    this.stoppedByUser = false;

    void this.stopping
      .then(() => this.connection.start())
      .then(() => this.syncRoomsGroups())
      .catch(() => {
        this.started = false;
        this.scheduleReconnect();
      });
  }

  /**
   * Se llama al terminar la sesión (DefaultLayoutComponent.ngOnDestroy — todos los caminos de
   * logout navegan a /login, fuera del layout). Imprescindible en modo JWT: el logout navega sin
   * recargar la página, y como este servicio es root, sin esto la conexión seguiría viva y
   * autenticada como el usuario anterior (el token solo se usa al negociar) — el siguiente usuario
   * que entrara en la misma pestaña recibiría los mensajes de las salas del anterior.
   */
  disconnect(): void {
    this.stoppedByUser = true;
    this.started = false;

    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }

    // Mismo motivo que ChatService.clearRooms() en logout: sin esto, el estado de presencia del
    // usuario anterior (A2) seguiría visible un instante para el siguiente que entre en la misma
    // pestaña, hasta que la primera recarga de salas lo pisara.
    this.onlineUsersState.set(new Map());

    this.stopping = this.connection.stop().catch(() => {
      // Si ya estaba cerrada o el cierre falla, no hay nada más que hacer.
    });
  }

  /** Debe invocarse tras cambiar de instalación en el filtro de la navbar. */
  syncRoomsGroups(): void {
    if (this.connection.state !== HubConnectionState.Connected) return;

    void this.connection.invoke(SYNC_ROOMS_GROUPS_METHOD).catch(() => {
      // La reconexión automática volverá a sincronizar los grupos.
    });
  }

  private scheduleReconnect(): void {
    this.started = false;
    if (this.stoppedByUser || this.reconnectTimerId !== null) return;

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      this.connect();
    }, INITIAL_RECONNECT_DELAY_MS);
  }
}
